import { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { api } from '../../lib/api';

type Reg = {
  id: number; serialNumber: string; status: string; createdAt: string;
  purchaseDate: string | null; warrantyEndDate: string | null;
  user: { email: string; firstName: string | null; lastName: string | null } | null;
  product: { name: string } | null;
  distributor: { name: string } | null;
};

export default function AdminRegistrations() {
  const [regs, setRegs] = useState<Reg[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [transferId, setTransferId] = useState<number | null>(null);
  const [transferEmail, setTransferEmail] = useState('');
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    api.get<Reg[]>('/admin/registrations?limit=200').then(setRegs).finally(() => setLoading(false));
  }, []);

  const filtered = regs.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q || r.serialNumber.toLowerCase().includes(q) ||
      (r.product?.name ?? '').toLowerCase().includes(q) ||
      (r.user?.email ?? '').toLowerCase().includes(q);
    const matchStatus = !statusFilter || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  function exportCSV() {
    const token = localStorage.getItem('mw_token');
    const apiBase = (import.meta as any).env?.VITE_API_URL ?? '';
    fetch(`${apiBase}/admin/registrations/export`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'registrations.csv'; a.click();
        URL.revokeObjectURL(url);
      });
  }

  async function voidReg(id: number) {
    if (!confirm('Void this registration?')) return;
    await api.patch(`/admin/registrations/${id}/status`, { status: 'voided' });
    setRegs(p => p.map(r => r.id === id ? { ...r, status: 'voided' } : r));
  }

  async function doTransfer() {
    if (!transferId || !transferEmail.trim()) return;
    setTransferring(true);
    try {
      await api.post(`/admin/registrations/${transferId}/transfer`, { newEmail: transferEmail.trim() });
      setRegs(p => p.map(r => r.id === transferId ? { ...r, status: 'transferred' } : r));
      setTransferId(null); setTransferEmail('');
    } catch (e: any) { alert(e.message ?? 'Transfer failed.'); }
    finally { setTransferring(false); }
  }

  const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
    active:      { bg: '#dcfce7', color: '#15803d' },
    voided:      { bg: '#f3f4f6', color: '#6b7280' },
    expired:     { bg: '#fee2e2', color: '#b91c1c' },
    transferred: { bg: '#dbeafe', color: '#1d4ed8' },
  };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-bold" style={{ color: '#0b0d0e' }}>Registrations</h1>
        <button onClick={exportCSV} style={{ fontSize: 12, padding: '6px 14px', border: '1px solid #e5e5e5', borderRadius: 6, background: '#fff', cursor: 'pointer', color: '#374151' }}>
          Export CSV
        </button>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <input className="input" style={{ maxWidth: 280 }} placeholder="Search serial, product or email…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <select className="input" style={{ maxWidth: 180 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {['active','voided','expired','transferred'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span style={{ fontSize: 13, color: '#8f9b9b', alignSelf: 'center' }}>{filtered.length} results</span>
      </div>

      {/* Transfer modal */}
      {transferId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setTransferId(null)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 360, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontWeight: 700, marginBottom: 12, color: '#0b0d0e' }}>Transfer registration</h3>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>Enter the email address of the user to transfer registration #{transferId} to.</p>
            <input className="input w-full mb-3" placeholder="user@example.com" value={transferEmail} onChange={e => setTransferEmail(e.target.value)} />
            <div className="flex gap-3">
              <button className="btn-primary text-sm flex-1" onClick={doTransfer} disabled={transferring || !transferEmail.trim()}>
                {transferring ? 'Transferring…' : 'Transfer'}
              </button>
              <button onClick={() => setTransferId(null)} style={{ flex: 1, border: '1px solid #e5e5e5', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl overflow-hidden" style={{ background: '#fff', border: '1px solid #e5e5e5' }}>
        {loading ? <p style={{ padding: 24, color: '#8f9b9b', fontSize: 14 }}>Loading…</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', minWidth: 640 }}>
              <thead style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                <tr style={{ textAlign: 'left' }}>
                  {['Product / Serial', 'User', 'Distributor', 'Warranty end', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#8f9b9b' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const s = STATUS_COLORS[r.status] ?? { bg: '#f3f4f6', color: '#374151' };
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid #fafafa' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <p style={{ fontWeight: 500, color: '#0b0d0e' }}>{r.product?.name ?? '—'}</p>
                        <p style={{ fontSize: 11, color: '#8f9b9b', fontFamily: 'monospace' }}>{r.serialNumber}</p>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <p style={{ color: '#374151' }}>{[r.user?.firstName, r.user?.lastName].filter(Boolean).join(' ') || '—'}</p>
                        <p style={{ fontSize: 11, color: '#8f9b9b' }}>{r.user?.email}</p>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#8f9b9b', fontSize: 12 }}>{r.distributor?.name ?? '—'}</td>
                      <td style={{ padding: '12px 16px', color: '#374151', fontSize: 12 }}>
                        {r.warrantyEndDate ? (
                          <span style={{ color: new Date(r.warrantyEndDate) < new Date() ? '#dc2626' : '#374151' }}>
                            {r.warrantyEndDate}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999 }}>{r.status}</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div className="flex gap-3">
                          {r.status === 'active' && (
                            <>
                              <button onClick={() => voidReg(r.id)} style={{ fontSize: 12, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Void</button>
                              <button onClick={() => { setTransferId(r.id); setTransferEmail(''); }} style={{ fontSize: 12, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Transfer</button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#8f9b9b' }}>No registrations found.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
